import { fetchBlockTree } from '@/lib/blocks/api';
import type { BlockRecord } from '@/lib/blocks/types';

import type { ContextScopeRoot, ContextSet } from './types';

export type ScopeTreeNode = {
  id: string;
  title: string;
  type: 'page' | 'database';
  children: ScopeTreeNode[];
};

let cache: ScopeTreeNode[] | null = null;
let cachePromise: Promise<ScopeTreeNode[]> | null = null;

function nodeTitle(block: BlockRecord): string {
  return block.title?.trim() || 'Untitled';
}

async function buildNode(block: BlockRecord): Promise<ScopeTreeNode> {
  const children = await fetchBlockTree(block.id);
  const childNodes: ScopeTreeNode[] = [];
  for (const child of children) {
    if (child.type !== 'page' && child.type !== 'database') {
      continue;
    }
    childNodes.push(await buildNode(child));
  }
  return {
    id: block.id,
    title: nodeTitle(block),
    type: block.type === 'database' ? 'database' : 'page',
    children: childNodes,
  };
}

export async function loadScopeTree(): Promise<ScopeTreeNode[]> {
  if (cache) {
    return cache;
  }
  if (cachePromise) {
    return cachePromise;
  }
  cachePromise = (async () => {
    const roots = await fetchBlockTree(null);
    const nodes: ScopeTreeNode[] = [];
    for (const root of roots) {
      if (root.type !== 'page' && root.type !== 'database') {
        continue;
      }
      nodes.push(await buildNode(root));
    }
    cache = nodes;
    return nodes;
  })();
  return cachePromise;
}

export function invalidateScopeTreeCache(): void {
  cache = null;
  cachePromise = null;
}

export function isDiscoveryEnabled(contextSet: ContextSet): boolean {
  return contextSet.discovery !== 'off';
}

export function isScopeRootSelected(roots: ContextScopeRoot[], blockId: string): boolean {
  return roots.some((root) => root.blockId === blockId);
}

export function discoveryForScope(
  enabled: boolean,
  roots: ContextScopeRoot[],
): ContextSet['discovery'] {
  if (!enabled) {
    return 'off';
  }
  return roots.length > 0 ? 'scoped' : 'open';
}

export function toggleScopeRoot(
  roots: ContextScopeRoot[],
  node: { id: string; title: string },
): ContextScopeRoot[] {
  if (isScopeRootSelected(roots, node.id)) {
    return roots.filter((root) => root.blockId !== node.id);
  }
  return [...roots, { blockId: node.id, title: node.title }];
}

export function withDiscoveryEnabled(contextSet: ContextSet, enabled: boolean): ContextSet {
  const roots = contextSet.scopeRoots ?? [];
  return { ...contextSet, discovery: discoveryForScope(enabled, roots) };
}

export function withScopeRoots(contextSet: ContextSet, roots: ContextScopeRoot[]): ContextSet {
  const enabled = isDiscoveryEnabled(contextSet);
  return { ...contextSet, scopeRoots: roots, discovery: discoveryForScope(enabled, roots) };
}
